import md5 from 'blueimp-md5'

import { createSelector } from '@app/utils/createSelector'

import { type AuthSessionSnapshot } from '@app/packages/api/AuthSessionManager'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

const supervisionSlice = createReduxSlice<{ access_token: string }>('supervision')
const v3SessionSlice = createReduxSlice<{ snapshot: AuthSessionSnapshot }>('v3_session')
const sessionTeardownSlice = createReduxSlice<true>('session_teardown')
const sessionLostSlice = createReduxSlice<true>('session_lost')

/** Token of the supervised user, present when the main user acts on someone else's behalf. */
export const supervisedAccessTokenSelector = createSelector([supervisionSlice.selector], slice => slice?.access_token ?? null)

export const supervisorSelector = createSelector([supervisionSlice.selector], slice => slice ?? null)

export const isSupervisedSelector = createSelector([supervisionSlice.selector], slice => !!slice)

export const v3SessionSelector = createSelector([v3SessionSlice.selector], slice => slice?.snapshot)

/**
 * The effective JWT to authenticate as: the supervised user when supervising,
 * otherwise the main v3 session. A bare legacy token is never a credential — it
 * is exchanged into a v3 session up front (see the server bootstrap), so the
 * store only ever reflects a logged-in user once a real JWT exists.
 */
export const effectiveAccessTokenSelector = createSelector(
  [supervisedAccessTokenSelector, v3SessionSelector],
  (supervised, snapshot) => supervised ?? snapshot?.access_token ?? null
)

/**
 * A stable per-user identity for cache keys. Unlike the access token it does not
 * change when the JWT is refreshed (it keys on the subject's `user_id`), so it
 * does not trigger spurious refetches mid-session.
 */
export const authIdentitySelector = createSelector([supervisedAccessTokenSelector, v3SessionSelector], (supervised, snapshot) => {
  if (supervised) return `supervised:${md5(supervised)}`
  if (snapshot) return `user:${snapshot.user_id}`
  return null
})

export const isAuthenticatedSelector = createSelector([authIdentitySelector], identity => identity !== null)

export const accessToken8LastLettersSelector = createSelector([effectiveAccessTokenSelector], token => (token ? token.substring(token.length - 8) : null))

export const sessionSelector = createSelector([effectiveAccessTokenSelector, supervisorSelector], (access_token, supervisor) => ({ access_token, supervisor }))

export const setSupervisionSlice = (access_token: string) => supervisionSlice.set({ access_token })

export const clearSupervisionSlice = () => supervisionSlice.remove()

/**
 * Compared structurally, not by reference: every path that applies a session builds a fresh
 * snapshot object, and 22 selectors read this slice — the broadcast handler applies one per
 * open tab, so an unchanged session would notify all of them for nothing.
 */
export const setV3SessionSlice = (snapshot: AuthSessionSnapshot) =>
  createThunk((dispatch, getState) => {
    const current = v3SessionSelector(getState())
    if (current && current.access_token === snapshot.access_token && current.expires_at === snapshot.expires_at && current.user_id === snapshot.user_id) return
    dispatch(v3SessionSlice.set({ snapshot }))
  })

export const clearV3SessionSlice = () => v3SessionSlice.remove()

/**
 * Set once a logout has begun, and never unset — the page is on its way out. Everything after it
 * is teardown: the cookies are already gone but React keeps rendering until `location.replace`
 * lands, so a selector that loads on render fires once more against a dead session.
 */
export const sessionTeardownSelector = createSelector([sessionTeardownSlice.selector], slice => !!slice)

export const markSessionTeardown = () => sessionTeardownSlice.set(true)

/** This request found the session dead, so a route that needs sign-in can say why it sends the reader to /login. */
export const sessionLostSelector = createSelector([sessionLostSlice.selector], slice => !!slice)

export const markSessionLost = () => sessionLostSlice.set(true)
